// The one place Frontend-web turns "which currency" into the sign a person reads.
//
// WHY THIS FILE EXISTS (P24). Every price display used to pick its own sign, and
// they disagreed: the calendar hover card compared the appointment's `currency`
// with the STRING 'INR' — but the API sends an OBJECT
// ({ currencySymbol, currencyCode }), which is never equal to a string, so every
// Indian clinic's ₹500 visit read "€ 500". The ledger, the booking modals and
// the desk modal each had a third and fourth variant of the same guess.
//
// The currency reaches the client in three shapes, all handled here:
//   · an object from the API        { currencyCode: 'INR', currencySymbol: '₹' }
//                                   (appointments[].currency, doctor.country)
//   · a legacy string code          'INR', 'eur'
//   · a legacy string that is already a sign  '₹'
//
// The CODE decides the sign when there is one: a stored currencySymbol is free
// text somebody typed into a master, while the ISO code is what money is actually
// charged in. Intl knows the narrow sign of every ISO 4217 code (₹, €, $, £, ¥…);
// the stored symbol is used only for a code Intl does not recognise.

const CODE_RE = /^[A-Za-z]{3}$/;

// Intl's answer, memoised — building a NumberFormat per table row is wasteful.
const signCache = new Map();

const intlSign = (code) => {
    const upper = code.toUpperCase();
    if (signCache.has(upper)) return signCache.get(upper);
    let sign = '';
    try {
        const parts = new Intl.NumberFormat('en', {
            style: 'currency',
            currency: upper,
            currencyDisplay: 'narrowSymbol',
        }).formatToParts(0);
        sign = parts.find((p) => p.type === 'currency')?.value || '';
    } catch {
        // An invalid code ("XX1"), or a runtime without narrowSymbol support.
        sign = '';
    }
    // Intl echoes an unknown-but-well-formed code back ("ABC"); that is not a sign.
    if (sign.toUpperCase() === upper) sign = '';
    signCache.set(upper, sign);
    return sign;
};

/** The ISO code a currency value carries, upper-cased, or '' when it has none. */
export const currencyCodeOf = (currency) => {
    if (!currency) return '';
    if (typeof currency === 'object') {
        const code = currency.currencyCode ?? currency.code;
        return typeof code === 'string' && CODE_RE.test(code.trim()) ? code.trim().toUpperCase() : '';
    }
    if (typeof currency === 'string' && CODE_RE.test(currency.trim())) return currency.trim().toUpperCase();
    return '';
};

/**
 * The sign to print in front of an amount.
 * @param {object|string|null|undefined} currency  any of the three shapes above
 * @param {string} [fallback='']                    what to print when nothing is known
 */
export const currencySymbol = (currency, fallback = '') => {
    if (currency === null || currency === undefined || currency === '') return fallback;

    const code = currencyCodeOf(currency);
    if (code) {
        const sign = intlSign(code);
        if (sign) return sign;
    }

    if (typeof currency === 'object') {
        const stored = currency.currencySymbol ?? currency.symbol;
        if (typeof stored === 'string' && stored.trim()) return stored.trim();
        return code || fallback;
    }

    if (typeof currency === 'string') return currency.trim() || fallback;
    return fallback;
};

/**
 * "₹ 500" / "€ 12.50" — sign, a space, then the amount.
 * @param {number|string} amount
 * @param {object|string} currency
 * @param {{ decimals?: number, fallback?: string }} [opts]
 *        decimals: fixed decimals (e.g. 2 on the ledger); omitted = amount as given.
 *        fallback: printed for a missing / non-numeric amount (default '—').
 */
export const formatPrice = (amount, currency, { decimals, fallback = '—' } = {}) => {
    if (amount === null || amount === undefined || amount === '') return fallback;
    let shown = amount;
    if (typeof decimals === 'number') {
        const n = Number(amount);
        if (!Number.isFinite(n)) return fallback;
        shown = n.toFixed(decimals);
    }
    const sign = currencySymbol(currency);
    return sign ? `${sign} ${shown}` : String(shown);
};

// ---- A doctor's currency, for screens that price by the doctor, not the visit ----
//
// The doctor record carries `country` as the API's country object
// ({ countryName, currencyCode, currencySymbol, … }). The dashboards, the
// specialities tab and the doctor modals each used to guess from it on their
// own — one by the country NAME ("India" → ₹), six by the doctor's TIME ZONE
// (Asia/Kolkata → ₹, everything else → €, so a doctor in Dubai earned euros).
// The guess now lives here, once, and only as a last resort: the country's own
// currency code decides whenever the record has one.
const COUNTRY_NAME_CURRENCY = { india: 'INR', france: 'EUR' };
const ZONE_CURRENCY = {
    'asia/kolkata': 'INR',
    'asia/calcutta': 'INR',
    // A misspelling some older doctor rows were saved with; the screens matched it.
    'asia/kolkattta': 'INR',
    'europe/paris': 'EUR',
};

/**
 * The currency to price a doctor's fees in: the country object when it names a
 * currency, else an ISO code inferred from the country name or the doctor's
 * zone, else null.
 */
export const doctorCurrency = (doctor) => {
    if (!doctor || typeof doctor !== 'object') return null;
    const country = doctor.country;
    if (country && typeof country === 'object') {
        if (currencySymbol(country)) return country;
    }
    const name = typeof country === 'object' && country
        ? (country.countryName || country.countryname || '')
        : (typeof country === 'string' ? country : '');
    const byName = COUNTRY_NAME_CURRENCY[String(name).trim().toLowerCase()];
    if (byName) return byName;
    const byZone = ZONE_CURRENCY[String(doctor.timeZone || '').trim().toLowerCase()];
    return byZone || null;
};

/** The sign for a doctor's fees ("₹", "€", …), or `fallback` when nothing is known. */
export const doctorCurrencySymbol = (doctor, fallback = '') => currencySymbol(doctorCurrency(doctor), fallback);

/**
 * The one sign a set of doctors' figures share — for an admin total summed over
 * several doctors. Each row is read by `doctorCurrencySymbol` (rows carry the
 * country object or just its name); rows that name no currency are ignored.
 * Returns '' when they disagree or none is known: a total over ₹ and € has no
 * honest single sign.
 * @param {Array<{ country?: object|string, timeZone?: string }>} rows
 */
export const sharedDoctorCurrencySymbol = (rows) => {
    const signs = new Set((Array.isArray(rows) ? rows : [])
        .map((r) => doctorCurrencySymbol(r))
        .filter(Boolean));
    return signs.size === 1 ? [...signs][0] : '';
};

/**
 * A money figure for charts and cards: sign, a space, the amount grouped the
 * reader's way ("₹ 12,500"). Just the grouped number when no sign is known —
 * never someone else's sign.
 * @param {number|string} amount
 * @param {object|string|null} currency   any shape currencySymbol accepts
 * @param {{ suffix?: string }} [opts]    e.g. '/h', 'k'
 */
export const formatMoney = (amount, currency, { suffix = '' } = {}) => {
    const n = Number(amount) || 0;
    const sign = currencySymbol(currency);
    const shown = `${n.toLocaleString()}${suffix}`;
    return sign ? `${sign} ${shown}` : shown;
};

/**
 * The hospital's currency for the admin dashboard's totals, from the sources
 * that page holds, in order of how directly each names it (P24):
 *   1. `tenantCurrency` — the tenant's country from the country master
 *      (hooks/useHospitalCurrency.js);
 *   2. the tenant country's row in the snapshot's `earningsByCountry`. That list
 *      has one row per country the hospital has doctors in, each with its
 *      country's currency. Only the row for the TENANT's country is taken —
 *      never simply the first, which for a hospital with doctors in two
 *      countries would price every total in whichever sorted first;
 *   3. the admin's own `country` object (admins inherit the tenant's country);
 *   4. the one sign every leaderboard doctor shares.
 * Returns null when none of them names a currency.
 */
export const hospitalCurrencyFrom = ({ tenantCurrency, tenantCountryId, earningsByCountry, admin, leaderboard } = {}) => {
    if (tenantCurrency && currencySymbol(tenantCurrency)) return tenantCurrency;
    const rows = Array.isArray(earningsByCountry?.data) ? earningsByCountry.data : [];
    const ownRow = tenantCountryId ? rows.find((r) => r?.countryId === tenantCountryId) : null;
    if (ownRow && currencySymbol(ownRow)) return ownRow;
    if (admin?.country && typeof admin.country === 'object' && currencySymbol(admin.country)) return admin.country;
    return sharedDoctorCurrencySymbol(leaderboard?.data) || null;
};

// ---- Subscription prices: the platform's money, not the hospital's ----------
//
// A doctor's plan is sold by the PLATFORM, so its price is not in the hospital's
// currency. What the backend actually stores (re-checked 22 Sep 2026):
//   · a payment row (`subscription_payments.currency`) names its currency — the
//     backend writes the platform currency on every row it records;
//   · a PACKAGE row, the status's `pending` block and the admin list's
//     synthesised pending rows carry NO currency field at all. Their price is
//     invoiced in the same platform currency, so they read it from here.
// So every subscription screen reads the row's own `currency` first and falls
// back to SUBSCRIPTION_CURRENCY — this one constant, instead of a sign typed
// into eight files. When packages gain a currency column, the screens follow
// the row without another edit.
//
// INR since the owner's decision of 22 Sep 2026 (Q8): plans are sold in India,
// in rupees, with no payment gateway — a doctor REQUESTS a plan, an invoice
// follows, and an admin marks it paid. Rows written before that day still name
// EUR and still print in euros, because the row wins over the constant.
export const SUBSCRIPTION_CURRENCY = 'INR';

/** The currency a subscription row is priced in: its own, else the platform's. */
export const subscriptionCurrency = (row) => {
    const own = row && typeof row === 'object' ? row.currency : null;
    return own && currencySymbol(own) ? own : SUBSCRIPTION_CURRENCY;
};

/** The sign for a subscription row's price. */
export const subscriptionSymbol = (row) => currencySymbol(subscriptionCurrency(row));

/**
 * A subscription price as the plan screens print it — the sign straight against
 * the amount ("€49", "€1,200"). The amount is grouped only when asked, because
 * the plan cards show the admin's price exactly as it was typed.
 * @param {number|string} amount
 * @param {object|null} [row]            the package / payment / pending row it belongs to
 * @param {{ grouped?: boolean }} [opts]
 */
export const subscriptionPrice = (amount, row, { grouped = false } = {}) => {
    const shown = grouped ? (Number(amount) || 0).toLocaleString() : String(amount ?? '');
    return `${subscriptionSymbol(row)}${shown}`;
};

export default currencySymbol;
